import { useCallback } from 'react';

import { FlexProps, useBreakpointValue } from '@vimeo/bokeh';

import { isModifierOrMiddleClick } from '../utils/helper';
import { getGridTemplateBreakpointValue } from '../utils/style';
import { Clip, GetResponsiveValue, GridSize } from '../utils/types';
import { useVideoGrid } from './use-video-grid';

export interface ShowcasePlaylistHookProps {
  getResponsiveValue: GetResponsiveValue;
  gridSize?: GridSize;
  isMobilePreview?: boolean;
  isShowcasePreview?: boolean;
  onVideoCardClick?: (
    link: string,
    clipId: string,
    videoPrivacy: string,
    userUri: string,
    live?: Clip['live'],
  ) => void;
  showVideoPlayerCards?: boolean;
  isNewCustomizeControls: boolean;
}

/**
 * Hook for managing the showcase playlist layout and behavior.
 * @param {ShowcasePlaylistHookProps} props - The properties for the hook.
 * @param {GetResponsiveValue} props.getResponsiveValue - Function to get responsive values.
 * @param {GridSize} props.gridSize - The size of the grid (e.g., 's', 'm', 'l').
 * @param {boolean} [props.isMobilePreview] - Whether the playlist is in mobile preview mode.
 * @param {boolean} [props.isShowcasePreview] - Whether the playlist is in showcase preview mode.
 * @param {function} [props.onVideoCardClick] - Callback for video card click events.
 * @param {boolean} [props.showVideoPlayerCards] - Whether to show video player cards.
 * @param {boolean} [props.isNewCustomizeControls] - Whether to use new customize controls.
 * @returns An object containing layout properties and event handlers.
 */
export const useShowcasePlaylist = ({
  isMobilePreview,
  isShowcasePreview,
  onVideoCardClick,
  showVideoPlayerCards,
  isNewCustomizeControls,
  ...rest
}: ShowcasePlaylistHookProps) => {
  const direction = useBreakpointValue<FlexProps['flexDirection']>({
    base: showVideoPlayerCards ? 'column' : 'row',
    sm: isMobilePreview && !showVideoPlayerCards ? 'row' : 'column',
  });
  const gridTemplateColumns = useBreakpointValue(
    getGridTemplateBreakpointValue(showVideoPlayerCards),
  );
  const { effectiveGridSize, gridGap, numColumns } = useVideoGrid({
    isMobilePreview,
    showVideoPlayerCards,
    isNewCustomizeControls,
    ...rest,
  });

  const handleVideoCardClick = useCallback(
    (e: React.MouseEvent<HTMLDivElement, MouseEvent>, clip: Clip) => {
      if (isModifierOrMiddleClick(e)) {
        return;
      }
      e.preventDefault();
      onVideoCardClick?.(
        isShowcasePreview ? clip.uri : clip.link,
        clip.clipId || '',
        clip.privacy.view,
        clip.user.uri,
        clip.live,
      );
    },
    [isShowcasePreview, onVideoCardClick],
  );

  return {
    direction: isNewCustomizeControls ? 'column' : direction,
    effectiveGridSize,
    gridGap,
    gridTemplateColumns,
    handleVideoCardClick,
    numColumns,
  };
};
